import { useEffect, useState } from "react";
import { BASELINE_LEAF_NAME, genId } from "../../utils/draftutils";
import {
  dominiValidi,
  getDescrizioneFromParent,
  maxAssegnabili,
  siblingNameSet,
  usedDomainValsByChildren,
  normName
} from "../../utils/gerarchiautils";

export default function ChildrenModal({ all, parent, onClose, onConfirm }) {
  const [numFigli, setNumFigli] = useState(1);
  const [modalError, setModalError] = useState("");

  const [figliFoglia, setFigliFoglia] = useState([]);
  const [figliNonfoglia, setFigliNonfoglia] = useState([]);

  useEffect(() => {
    setModalError("");
    setNumFigli(1);
    if (parent.tipoFigli === "foglia") {
      setFigliFoglia([{ nome: "", dominioVal: "", conversionFactor: "" }]);
      setFigliNonfoglia([]);
    } else {
      setFigliNonfoglia([{
        nome: "", campo: "", tipoFigli: "foglia", dominioVal: "",
        dominio: [{ valore: "", descrizione: "" }]
      }]);
      setFigliFoglia([]);
    }
  }, [parent]);

  const max = maxAssegnabili(all, parent);

  const changeNumFigli = (n) => {
    const count = Math.max(1, Math.min(n, max));
    setNumFigli(count);
    if (parent.tipoFigli === "foglia") {
      setFigliFoglia((prev) => {
        const copy = [...prev];
        if (count > copy.length) while (copy.length < count) copy.push({ nome: "", dominioVal: "", conversionFactor: "" });
        else copy.length = count;
        return copy;
      });
    } else {
      setFigliNonfoglia((prev) => {
        const copy = [...prev];
        if (count > copy.length) while (copy.length < count) copy.push({
          nome: "", campo: "", tipoFigli: "foglia", dominioVal: "",
          dominio: [{ valore: "", descrizione: "" }]
        });
        else copy.length = count;
        return copy;
      });
    }
  };

  const confirm = () => {
    setModalError("");
    const siblingNames = siblingNameSet(all, parent);
    const usedExist = new Set(usedDomainValsByChildren(all, parent));

    if (numFigli < 1 || numFigli > max) {
      setModalError(`Il numero di figli deve essere tra 1 e ${max}.`);
      return;
    }

    if (parent.tipoFigli === "foglia") {
      const nuovi = [];
      const namesSeen = new Set();
      for (let i = 0; i < numFigli; i++) {
        const f = figliFoglia[i] || {};
        const nName = normName(f.nome);
        if (!nName) return setModalError("Inserisci il nome per ogni foglia.");
        if (siblingNames.has(nName)) return setModalError(`Il nome "${f.nome}" è già usato tra i fratelli.`);
        if (namesSeen.has(nName)) return setModalError("I nomi tra le nuove foglie devono essere univoci.");
        namesSeen.add(nName);

        if (!f?.dominioVal) return setModalError("Seleziona il dominioVal per ogni foglia.");
        if (usedExist.has(f.dominioVal)) return setModalError("DominioVal già utilizzato da un altro figlio.");
        if (nuovi.some((x) => x.dominioVal === f.dominioVal))
          return setModalError("Ogni dominioVal deve essere unico tra le foglie inserite.");

        const cf = parseFloat(f.conversionFactor);
        if (!(cf >= 0.5 && cf <= 2))
          return setModalError(`Il fattore di conversione deve essere tra 0.5 e 2 (vs "${BASELINE_LEAF_NAME}").`);

        nuovi.push({
          tempId: genId(),
          idPadre: parent.tempId,
          isRadice: false,
          nome: f.nome.trim(),
          campo: null,
          tipoFigli: undefined,
          dominioVal: f.dominioVal,
          descrizione: getDescrizioneFromParent(parent, f.dominioVal),
          conversionFactor: cf,
          dominio: [],
          descrizioni: [],
          _createdAt: new Date().toISOString(),
        });
      }
      onConfirm(nuovi);
      return;
    }

    // nonfoglia
    const nuovi = [];
    const namesSeen = new Set();
    for (let i = 0; i < numFigli; i++) {
      const nf = figliNonfoglia[i] || {};
      const nName = normName(nf.nome);
      if (!nName) return setModalError("Inserisci il nome per ogni nonfoglia.");
      if (siblingNames.has(nName)) return setModalError(`Il nome "${nf.nome}" è già usato tra i fratelli.`);
      if (namesSeen.has(nName)) return setModalError("I nomi tra le nuove nonfoglie devono essere univoci.");
      namesSeen.add(nName);

      if (!nf?.campo?.trim()) return setModalError("Inserisci il campo per ogni nonfoglia.");
      if (!["foglia", "nonfoglia"].includes(nf?.tipoFigli))
        return setModalError("Seleziona il tipo dei figli per ogni nonfoglia.");
      if (!nf?.dominioVal) return setModalError("Seleziona il dominio per ogni nonfoglia.");
      if (usedExist.has(nf.dominioVal)) return setModalError("Dominio già utilizzato da un altro figlio.");
      if (nuovi.some((x) => x.dominioVal === nf.dominioVal))
        return setModalError("Ogni dominio deve essere unico tra le nonfoglie inserite.");

      const { ok, rows } = dominiValidi(nf.dominio || []);
      if (!ok) return setModalError("Per ogni nonfoglia inserisci almeno una coppia dominio/descrizione (o rimuovi righe vuote).");

      nuovi.push({
        tempId: genId(),
        idPadre: parent.tempId,
        isRadice: false,
        nome: nf.nome.trim(),
        campo: nf.campo.trim(),
        tipoFigli: nf.tipoFigli,
        dominioVal: nf.dominioVal,
        descrizione: getDescrizioneFromParent(parent, nf.dominioVal),
        conversionFactor: null,
        dominio: rows.map((r) => r.valore),
        descrizioni: rows.map((r) => r.descrizione),
        _createdAt: new Date().toISOString(),
      });
    }
    onConfirm(nuovi);
  };

  // domini disponibili dinamici per ogni riga (evita duplicati anche entro il form)
  const selectOptions = (i) => {
    const usati = new Set(usedDomainValsByChildren(all, parent));
    if (parent.tipoFigli === "foglia") {
      for (let k = 0; k < i; k++) {
        const sel = figliFoglia[k]?.dominioVal;
        if (sel) usati.add(sel);
      }
    } else {
      for (let k = 0; k < i; k++) {
        const sel = figliNonfoglia[k]?.dominioVal;
        if (sel) usati.add(sel);
      }
    }
    return (parent.dominio || []).filter((v) => !usati.has(v));
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", padding: 16, borderRadius: 8, minWidth: 620, maxHeight: "90vh", overflow: "auto" }}
      >
        <h3 style={{ marginTop: 0 }}>Aggiungi figli a: <span style={{
            color: "green", fontWeight: "bold"}}>{parent.nome}</span></h3>

        <label>
          Numero di categorie figlie che vuoi inserire (1…{max}):
          <input
            type="number"
            min={1}
            max={max}
            value={numFigli}
            onChange={(e) => changeNumFigli(Number(e.target.value || 1))}
            style={{ marginLeft: 8, width: 80 }}
          />
        </label>

        {parent.tipoFigli === "foglia" ? (
          <>
            <p style={{ marginTop: 8 }}>
               Indica il fattore di conversione (0,5–2) vs "Ripetizioni di matematica".
            </p>
            <p>
               <em>es. fattore 2: per 1 ora del servizio aggiunto bisogna offrire 2 ore di matematica</em>
            </p>
            {Array.from({ length: numFigli }).map((_, i) => {
              const disponibili = selectOptions(i);
              return (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 240px 140px", gap: 8, marginBottom: 6, alignItems: "center" }}>
                  <input
                    type="text"
                    placeholder={`Nome servizio #${i + 1}`}
                    value={figliFoglia[i]?.nome || ""}
                    onChange={(e) => setFigliFoglia(p => p.map((row, idx) => idx===i ? { ...row, nome: e.target.value } : row))}
                  />
                  <select
                    value={figliFoglia[i]?.dominioVal || ""}
                    onChange={(e) => setFigliFoglia(p => p.map((row, idx) => idx===i ? { ...row, dominioVal: e.target.value } : row))}
                  >
                    <option value="">dominio </option>
                    {disponibili.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                  <input
                    type="number"
                    placeholder="Fattore"
                    min={0.5}
                    max={2}
                    step={0.1}
                    value={figliFoglia[i]?.conversionFactor ?? ""}
                    onChange={(e) => setFigliFoglia(p => p.map((row, idx) => idx===i ? { ...row, conversionFactor: e.target.value } : row))}
                    title="Fattore 0,5–2"
                  />
                  <div style={{ gridColumn: "1 / -1", fontSize: 12, color: "#555" }}>
                    {figliFoglia[i]?.dominioVal
                      ? getDescrizioneFromParent(parent, figliFoglia[i].dominioVal)
                      : ""}
                  </div>
                </div>
              );
            })}
          </>
        ) : (
          <>
            <p style={{ marginTop: 8 }}></p>
            {Array.from({ length: numFigli }).map((_, i) => {
              const nf = figliNonfoglia[i] || {};
              const disponibili = selectOptions(i);
              const descrSel = nf.dominioVal ? getDescrizioneFromParent(parent, nf.dominioVal) : "";

              return (
                <div key={i} style={{ border: "1px solid #ddd", padding: 10, borderRadius: 6, marginBottom: 8 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input
                      type="text"
                      placeholder={`Nome raggruppamento #${i + 1}`}
                      value={nf.nome || ""}
                      onChange={(e) => setFigliNonfoglia(p => p.map((row, idx) => idx===i ? { ...row, nome: e.target.value } : row))}
                      style={{ flex: 1 }}
                    />
                    <input
                      type="text"
                      placeholder="Campo"
                      value={nf.campo || ""}
                      onChange={(e) => setFigliNonfoglia(p => p.map((row, idx) => idx===i ? { ...row, campo: e.target.value } : row))}
                      style={{ flex: 1 }}
                    />
                    <select
                      value={nf.tipoFigli || "foglia"}
                      onChange={(e) => setFigliNonfoglia(p => p.map((row, idx) => idx===i ? { ...row, tipoFigli: e.target.value } : row))}
                      style={{ width: 180 }}
                    >
                      <option value="foglia">Figli: foglia</option>
                      <option value="nonfoglia">Figli: nonfoglia</option>
                    </select>
                  </div>

                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
                    <select
                      value={nf.dominioVal || ""}
                      onChange={(e) => setFigliNonfoglia(p => p.map((row, idx) => idx===i ? { ...row, dominioVal: e.target.value } : row))}
                      style={{ width: 260 }}
                    >
                      <option value="">valore dal dominio del padre </option>
                      {disponibili.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>

                  <div style={{ marginTop: 8 }}>
                    <strong>Inserisci i valori di dominio che assumeranno le categorie figlie (1 per figlio):</strong>
                    {(nf.dominio || []).map((r, rIdx) => (
                      <div key={rIdx} style={{ display: "flex", gap: 6, marginTop: 6 }}>
                        <input
                          type="text"
                          placeholder="Valore dominio"
                          value={r.valore || ""}
                          onChange={(e) => setFigliNonfoglia(p => p.map((row, idx) => idx===i ? {
                            ...row,
                            dominio: row.dominio.map((rr, k) => k===rIdx ? { ...rr, valore: e.target.value } : rr)
                          } : row))}
                          style={{ flex: 1 }}
                        />
                        <input
                          type="text"
                          placeholder="Descrizione"
                          value={r.descrizione || ""}
                          onChange={(e) => setFigliNonfoglia(p => p.map((row, idx) => idx===i ? {
                            ...row,
                            dominio: row.dominio.map((rr, k) => k===rIdx ? { ...rr, descrizione: e.target.value } : rr)
                          } : row))}
                          style={{ flex: 1 }}
                        />
                        <button type="button" onClick={() => setFigliNonfoglia(p => p.map((row, idx) =>
                          idx===i ? { ...row, dominio: row.dominio.filter((_, k) => k !== rIdx) } : row
                        ))}>
                          Rimuovi
                        </button>
                      </div>
                    ))}
                    <button type="button" style={{ marginTop: 6 }} onClick={() => setFigliNonfoglia(p => p.map((row, idx) =>
                      idx===i ? { ...row, dominio: [...row.dominio, { valore: "", descrizione: "" }] } : row
                    ))}>
                      + Aggiungi valore di dominio
                    </button>
                  </div>
                </div>
              );
            })}
          </>
        )}

        {modalError && <p style={{ color: "red" }}>{modalError}</p>}

        <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
          <button onClick={onClose}>Annulla</button>
          <button onClick={confirm}>Conferma</button>
        </div>
      </div>
    </div>
  );
}
